/**
 * ProtectedSuperAdminRoute - Route Guard for SuperAdmin
 *
 * Architecture: Component → Hook (TanStack Query) → Service → API
 * ✅ Utilise useQuery pour vérification auth
 * ✅ useState uniquement pour état UI
 */

import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { Shield, AlertTriangle } from 'lucide-react';
import { getAccessToken, setAccessToken } from '@/services/tokenManager';
import { authAPI, sessionsAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
import type { ReactNode } from 'react';

interface ProtectedSuperAdminRouteProps {
  children: ReactNode;
}

const ProtectedSuperAdminRoute = ({ children }: ProtectedSuperAdminRouteProps) => {
  const { user, isAuthenticated, setUser } = useAuthStore();
  const navigate = useNavigate();

  // ✅ TanStack Query pour vérification/refresh de session
  const { isLoading, isError } = useQuery({
    queryKey: ['auth', 'verify-superadmin'],
    queryFn: async () => {
      let token = getAccessToken();

      // Si pas de token en mémoire, tenter un refresh via cookie HttpOnly
      if (!token) {
        logger.log('🔐 SuperAdmin: Pas de token, tentative de refresh...');
        const refreshResult = await sessionsAPI.refresh();
        if (refreshResult?.accessToken) {
          const expiresIn = refreshResult.expiresIn || 900;
          setAccessToken(refreshResult.accessToken, expiresIn);
          token = refreshResult.accessToken;
          logger.log('✅ SuperAdmin: Token restauré via cookie');
        } else {
          throw new Error('No valid session');
        }
      }

      // Si on a un token mais pas de user dans le store, charger le user
      if (token && !user) {
        logger.log('🔐 SuperAdmin: Chargement des données utilisateur...');
        const userData = await authAPI.me();
        if (userData) {
          setUser(userData);
          logger.log('✅ SuperAdmin: User chargé:', userData.email);
          return userData;
        }
        throw new Error('Failed to load user');
      }

      return user;
    },
    retry: false,
    staleTime: 30 * 1000, // 30 secondes
    enabled: !isAuthenticated || !user, // Ne lance que si pas déjà authentifié
  });

  // Afficher un loader pendant la vérification
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Vérification des permissions...</p>
        </div>
      </div>
    );
  }

  // Si erreur ou pas authentifié → login
  if (isError || !isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Vérifier le rôle super admin
  if (user.role !== 'SUPERADMIN') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h1>
          <p className="text-gray-600 mb-6">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
            Seuls les super administrateurs peuvent accéder au panneau d'administration.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-300"
            >
              Retour au dashboard
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-300"
            >
              Retour
            </button>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <strong>Note :</strong> Cette tentative d'accès a été enregistrée dans les logs de sécurité.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Utilisateur authentifié et super admin
  return <>{children}</>;
};

export default ProtectedSuperAdminRoute;
